import { MessageBarType, Shimmer, ShimmerElementType } from '@fluentui/react';
import { Button, buttonStylesDanger, Checkbox, MessageBar, Pivot, useTheme } from '@h2oai/ui-kit';
import { useEffect, useState } from 'react';

import { RemoveTrustedRolesResponse } from '../../../../../admin-center/gen/trusted_roles/trusted_roles_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useEnv, useUser } from '../../../../../utils/hooks';
import { ResponseMessage } from '../../../common/types';
import { buildUserContext } from '../../../common/utils';
import {
  groupItemStyles,
  groupListHeaderStyles,
  messageBarContainerStyles,
  shimmerItemStyles,
} from '../../CustomGroupManagement/CustomGroupManagement.styles';
import {
  roleArnDisplayStyles,
  roleArnLabelStyles,
  roleArnValueStyles,
  rolesListDescriptionStyles,
  trustedRolesHeaderStyles,
  trustedRolesItemsContainer,
  trustedRolesListRoot,
  trustedRolesTitleStyles,
} from '../TrustedRoleManagement.styles';

type TrustedRolesListProps = {
  refreshKey: number;
  onMessage: (msgs: ResponseMessage[]) => void;
};

export default function TrustedRolesList({ refreshKey, onMessage }: TrustedRolesListProps) {
  const service = useAdminCenterService();
  const env = useEnv();
  const [daiRoles, setDaiRoles] = useState<string[]>([]);
  const [h2ogpteRoles, setH2ogpteRoles] = useState<string[]>([]);
  const [daiRoleArn, setDaiRoleArn] = useState<string>('');
  const [h2ogpteRoleArn, setH2ogpteRoleArn] = useState<string>('');
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentProduct, setCurrentProduct] = useState<string>('dai');
  const theme = useTheme();
  const user = useUser();

  // Check if H2OGPTE is available in the environment
  const h2oGpteAvailable = env?.menu?.externalMenuItems?.some(
    (item) => item.key === 'Enterpriseh2oGPTe' || item.name?.includes('h2oGPTe')
  );

  const h2oGpteEnabled = Boolean(h2oGpteAvailable);

  // Fetch roles on mount and when refreshKey or currentProduct changes
  useEffect(() => {
    const fetchRoles = async (product: string) => {
      try {
        setLoading(true);
        const request = {
          product: product,
        };
        const resp = await service.getTrustedRolesRequest(request);
        if (product === 'dai') {
          setDaiRoles(resp.trustedRoles || []);
          setDaiRoleArn(resp.roleArn || '');
        } else {
          setH2ogpteRoles(resp.trustedRoles || []);
          setH2ogpteRoleArn(resp.roleArn || '');
        }
      } catch (err) {
        setError('Failed to load trusted roles');
      } finally {
        setLoading(false);
      }
    };

    // Fetch both products' roles
    const fetchAllRoles = async () => {
      await fetchRoles('dai');
      // Only fetch h2ogpte roles if the service is available
      if (h2oGpteEnabled) {
        await fetchRoles('h2ogpte');
      }
    };

    fetchAllRoles();
  }, [service, refreshKey, h2oGpteEnabled]);

  const toggleSelect = (role: string) => {
    setSelectedRoles((prev) => {
      const copy = new Set(prev);
      if (copy.has(role)) {
        copy.delete(role);
      } else {
        copy.add(role);
      }
      return copy;
    });
  };

  const formatRemoveRolesResponse = (response: RemoveTrustedRolesResponse): ResponseMessage[] => {
    const messages: ResponseMessage[] = [];

    // Success roles
    if (response.successRoles && response.successRoles.length > 0) {
      const roleNames = response.successRoles.map((rs) => rs.role).join(', ');
      messages.push({
        type: MessageBarType.success,
        content: `Successfully removed: ${roleNames}`,
      });
    }

    // Skipped roles
    if (response.skippedRoles && response.skippedRoles.length > 0) {
      const roleDetails = response.skippedRoles.map((rs) => `${rs.role} (${rs.message})`).join(', ');
      messages.push({
        type: MessageBarType.warning,
        content: `Skipped: ${roleDetails}`,
      });
    }

    // Error roles
    if (response.errorRoles && response.errorRoles.length > 0) {
      const roleDetails = response.errorRoles.map((rs) => `${rs.role} (${rs.message})`).join(', ');
      messages.push({
        type: MessageBarType.error,
        content: `Error: ${roleDetails}`,
      });
    }

    return messages;
  };

  const handleDelete = async () => {
    if (selectedRoles.size === 0) return;
    try {
      const userContext = await buildUserContext(user);
      setLoading(true);
      const response = await service.removeTrustedRolesRequest({
        product: currentProduct,
        roleNames: Array.from(selectedRoles),
        userContext: userContext,
      });
      const messages = formatRemoveRolesResponse(response);

      if (messages.length > 0) {
        onMessage(messages);
      } else {
        onMessage([
          {
            type: MessageBarType.error,
            content: 'Failed to remove trusted roles',
          },
        ]);
      }

      // Update the correct roles array based on current product, only remove successfully deleted roles
      if (response.successRoles && response.successRoles.length > 0) {
        const successfullyRemovedRoles = new Set(response.successRoles.map((rs) => rs.role));
        if (currentProduct === 'dai') {
          setDaiRoles((prev) => prev.filter((r) => !successfullyRemovedRoles.has(r)));
        } else {
          setH2ogpteRoles((prev) => prev.filter((r) => !successfullyRemovedRoles.has(r)));
        }
      }
      setSelectedRoles(new Set());
    } catch (err) {
      onMessage([
        {
          type: MessageBarType.error,
          content: `Failed to delete trusted roles: ${err}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const renderShimmerItems = () => {
    return Array.from({ length: 6 }, (_, index) => (
      <div key={`shimmer-${index}`} style={shimmerItemStyles(theme)}>
        <Shimmer
          shimmerElements={[
            { type: ShimmerElementType.circle, width: 20, height: 20 },
            { type: ShimmerElementType.gap, width: 12 },
            { type: ShimmerElementType.line, width: `${Math.random() * 100 + 80}px`, height: 16 },
          ]}
        />
      </div>
    ));
  };

  const renderRolesList = (roles: string[], roleArn: string) => {
    return (
      <>
        {roleArn && (
          <div style={roleArnDisplayStyles(theme)}>
            <div style={roleArnLabelStyles(theme)}>Role ARN</div>
            <div style={roleArnValueStyles(theme)}>{roleArn}</div>
          </div>
        )}

        <div style={groupListHeaderStyles()}>
          <div>
            <p style={rolesListDescriptionStyles(theme)}>Select trusted roles to delete them.</p>
          </div>
          <Button
            text={loading ? 'Deleting...' : `Delete Selected (${selectedRoles.size})`}
            onClick={handleDelete}
            disabled={selectedRoles.size === 0 || loading}
            styles={buttonStylesDanger}
          />
        </div>

        <div style={trustedRolesItemsContainer()}>
          {loading ? (
            renderShimmerItems()
          ) : roles.length === 0 ? (
            <p>No trusted roles found. Create your first trusted role above.</p>
          ) : (
            roles.map((role) => (
              <div key={role} style={groupItemStyles(theme)} onClick={() => toggleSelect(role)}>
                <Checkbox
                  checked={selectedRoles.has(role)}
                  onChange={() => toggleSelect(role)}
                  styles={{
                    root: { marginRight: '12px' },
                  }}
                />
                <span
                  style={{
                    fontSize: '15px',
                    fontWeight: selectedRoles.has(role) ? '600' : '400',
                  }}
                >
                  {role}
                </span>
              </div>
            ))
          )}
        </div>
      </>
    );
  };

  return (
    <div style={trustedRolesListRoot(theme)}>
      {error && (
        <div style={messageBarContainerStyles()}>
          <MessageBar timeout={6000} onDismiss={() => setError(null)} messageBarType={MessageBarType.error}>
            {error}
          </MessageBar>
        </div>
      )}

      <div style={trustedRolesHeaderStyles()}>
        <h2 style={trustedRolesTitleStyles(theme)}>Configured Trusted Roles</h2>
      </div>

      <Pivot
        items={[
          {
            content: renderRolesList(daiRoles, daiRoleArn),
            headerText: 'DAI',
          },
          // Conditionally include h2oGPTe tab based on environment availability
          ...(h2oGpteEnabled
            ? [
                {
                  content: renderRolesList(h2ogpteRoles, h2ogpteRoleArn),
                  headerText: 'h2oGPTe',
                },
              ]
            : []),
        ]}
        onLinkClick={(item) => {
          setCurrentProduct(item?.props.itemKey === '1' ? 'h2ogpte' : 'dai');
          setSelectedRoles(new Set());
        }}
      />
    </div>
  );
}
